import React, { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'context/LanguageContext';
import { fetchProductsByCategory, resetProducts } from 'store/slices/productsSlice';
import ProductCard from 'features/products/components/ProductCard';
import BackButton from 'shared/components/BackButton';
import Loading from 'shared/components/Loading';
import { MdHome } from "react-icons/md";

export default function ProductsByCategoryPage() {
  const { categoryId: urlCategoryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, language } = useTranslation();
  const isRTL = language === "ar";
  const lastProductRef = useRef();
  
  const handleHomeClick = () => {
    navigate("/");
  }
  
  const {
    items: products,
    currentCategoryName: categoryName,
    currentPage,
    totalPages,
    loading,
    loadingMore,
    error
  } = useSelector((state) => state.products);

  const hasMore = currentPage < totalPages;

  useEffect(() => {
    if (!urlCategoryId) {
      return;
    }
    dispatch(resetProducts());
    dispatch(fetchProductsByCategory({ 
      categoryId: urlCategoryId, 
      page: 1 
    }));
  }, [urlCategoryId, dispatch]);

  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && hasMore && !loading && !loadingMore) {
      dispatch(fetchProductsByCategory({ 
        categoryId: urlCategoryId, 
        page: currentPage + 1 
      }));
    }
  }, [hasMore, loading, loadingMore, urlCategoryId, currentPage, dispatch]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "200px",
      threshold: 0
    });
    
    if (lastProductRef.current) {
      observer.observe(lastProductRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, products]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('goBack') || 'Go Back'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header with Back Button */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleHomeClick}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go to home"
            >
              <MdHome className="w-6 h-6 text-blue-600 mx-auto" />
            </button>
            <BackButton title={categoryName} />
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="min-h-screen bg-gray-50" dir={isRTL ? "rtl" : "ltr"}>
        <div className="container mx-auto px-4 pt-0 pb-6">
          {products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">{t('noProductsFound') || 'No products found'}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    className="cursor-pointer"
                    ref={index === products.length - 1 ? lastProductRef : null}
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>

              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
